import { useState } from 'react';
import { createPortal } from 'react-dom';
import { WidgetOverlay } from '../../components/WidgetOverlay';
import { EcobeeWidget } from '../kiosk/widgets/EcobeeWidget';
import {
  activeSetpoint,
  isEntityUnavailable,
  thermostatAction,
  useThermostat,
  type HvacAction,
  type ThermostatState,
} from '../kiosk/widgets/thermostatState';
import { CardFigure, HomeCard } from './HomeCard';
import type { TokenVar } from './wash';

// Indoor temperature on a little thermometer, tinted by what the system is
// doing: red heating, blue cooling, yellow holding at the setpoint. Opens the
// kiosk's thermostat dial in an overlay. Display only — nothing here changes
// the thermostat.

const POLL_MS = 60 * 1000;

interface Tint {
  fill: TokenVar;
  wash: TokenVar;
}

const TINT: Record<'heating' | 'cooling' | 'holding' | 'muted', Tint> = {
  heating: { fill: '--berg-red', wash: '--berg-red-soft' },
  cooling: { fill: '--berg-blue', wash: '--berg-blue-soft' },
  holding: { fill: '--berg-yellow', wash: '--berg-yellow-soft' },
  muted: { fill: '--berg-text-tertiary', wash: '--berg-neutral-ghost' },
};

function describe(t: ThermostatState | undefined): { caption: string; tint: Tint } {
  if (!t) return { caption: 'Loading…', tint: TINT.muted };
  if (isEntityUnavailable(t.state)) return { caption: 'Offline', tint: TINT.muted };
  const action: HvacAction | undefined = thermostatAction(t);
  const target = activeSetpoint(t, action);
  const to = target === null ? '' : ` ${Math.round(target)}°`;
  switch (action) {
    case 'heating':
      return { caption: `Heating to${to}`, tint: TINT.heating };
    case 'cooling':
      return { caption: `Cooling to${to}`, tint: TINT.cooling };
    case 'idle': {
      const { target_temp_low: low, target_temp_high: high } = t.attributes;
      const range =
        target === null && typeof low === 'number' && typeof high === 'number'
          ? ` ${Math.round(low)}–${Math.round(high)}°`
          : ` at${to}`;
      return { caption: `Holding${range}`, tint: TINT.holding };
    }
    case 'off':
      return { caption: 'Off', tint: TINT.muted };
    case 'fan':
      return { caption: 'Fan only', tint: TINT.muted };
    case 'drying':
      return { caption: 'Drying', tint: TINT.muted };
    default:
      return { caption: '', tint: TINT.muted };
  }
}

// The thermometer, in two sizes: the phone's 2×2 grid and the desktop's
// taller 4-across row. Ticks every 5°, 60–75° labelled; the tube runs off the
// card's bottom edge and the whole gauge fades out toward it.
interface GaugeGeometry {
  box: string; // container position + size
  mask: string;
  ticks: Array<{ label: number; y: number }>;
  label: string; // label position + type
  tick: string; // tick mark position + size
  tube: string; // tube position + size
  fill: string; // fill position + width
  setLine: string; // setpoint line position + width
  // y (px, container space) of a temperature, clamped to the tube; and the
  // y of the tube's bottom, for the fill height.
  y: (temp: number) => number;
  tubeBottom: number;
}

const PHONE_GAUGE: GaugeGeometry = {
  box: 'right-0.5 -bottom-5 w-[50px] h-32',
  mask: '[mask-image:linear-gradient(to_bottom,black_45%,transparent_84%)]',
  ticks: [
    { label: 75, y: 30 },
    { label: 70, y: 52 },
    { label: 65, y: 74 },
    { label: 60, y: 96 },
  ],
  label: 'left-7 w-5 text-[10px] leading-3 -translate-y-1/2',
  tick: 'left-[18px] w-1.5',
  tube: 'top-3 w-3.5 h-[148px]',
  fill: 'left-px w-3 -bottom-8',
  setLine: '-left-1 w-[22px]',
  y: (t) => Math.min(160, Math.max(12, 96 - (t - 60) * 4.4)),
  tubeBottom: 160,
};

const DESKTOP_GAUGE: GaugeGeometry = {
  box: 'right-7 -bottom-5 w-[60px] h-[200px]',
  mask: '[mask-image:linear-gradient(to_bottom,black_50%,transparent_88%)]',
  ticks: [
    { label: 75, y: 30 },
    { label: 70, y: 66 },
    { label: 65, y: 102 },
    { label: 60, y: 138 },
  ],
  label: 'left-9 w-6 text-xs leading-[14px] -translate-y-1/2',
  tick: 'left-6 w-2',
  tube: 'top-3 w-[18px] h-[220px]',
  fill: 'left-px w-4 -bottom-8',
  setLine: '-left-1 w-[26px]',
  y: (t) => Math.min(232, Math.max(12, 138 - (t - 60) * 7.2)),
  tubeBottom: 232,
};

function Gauge({
  g,
  temp,
  setpoint,
  tint,
  className,
}: {
  g: GaugeGeometry;
  temp: number | undefined;
  setpoint: number | null;
  tint: Tint;
  className: string;
}) {
  return (
    <span aria-hidden className={`absolute ${g.box} ${g.mask} ${className}`}>
      {g.ticks.map(({ label, y }) => (
        <span key={label}>
          <span className={`absolute ${g.label} text-fg-secondary`} style={{ top: y }}>
            {label}
          </span>
          <span
            className={`absolute ${g.tick} h-0.5 rounded-full bg-border-strong`}
            style={{ top: y - 1 }}
          />
        </span>
      ))}
      <span
        className={`absolute left-0 ${g.tube} rounded-full bg-subtle border border-border-strong`}
      />
      {typeof temp === 'number' && (
        <span
          className={`absolute ${g.fill} dot-glow animate-fill-up`}
          style={{
            height: g.tubeBottom - 1 - g.y(temp),
            backgroundColor: `var(${tint.fill})`,
            ['--dot-color' as string]: `var(${tint.fill})`,
          }}
        />
      )}
      {setpoint !== null && (
        <span
          className={`absolute ${g.setLine} h-0.5 rounded-full bg-fg`}
          style={{ top: g.y(setpoint) - 1 }}
        />
      )}
    </span>
  );
}

export function ThermostatCard() {
  const { thermostat } = useThermostat({ pollMs: POLL_MS, pauseWhenHidden: true });
  const [open, setOpen] = useState(false);

  const { caption, tint } = describe(thermostat);
  const temp = thermostat?.attributes.current_temperature;
  const setpoint = thermostat ? activeSetpoint(thermostat, thermostatAction(thermostat)) : null;
  const shown = typeof temp === 'number' ? String(Math.round(temp)) : '–';

  return (
    <>
      <HomeCard
        title="Thermostat"
        wash={tint.wash}
        ariaLabel={`Thermostat: ${shown}°, ${caption}`}
        onOpen={() => setOpen(true)}
        decoration={
          <>
            <Gauge
              g={PHONE_GAUGE}
              temp={temp}
              setpoint={setpoint}
              tint={tint}
              className="lg:hidden"
            />
            <Gauge
              g={DESKTOP_GAUGE}
              temp={temp}
              setpoint={setpoint}
              tint={tint}
              className="hidden lg:block"
            />
          </>
        }
      >
        <CardFigure value={shown} unit="°" unitPosition="after" caption={caption} />
      </HomeCard>

      {open &&
        createPortal(
          <WidgetOverlay
            onClose={() => setOpen(false)}
            sourceRect={null}
            label="Thermostat"
            header={<span className="text-sm font-semibold">Thermostat</span>}
            bodyClassName="flex-1 min-h-0 overflow-y-auto flex items-start justify-center"
          >
            <EcobeeWidget className="w-full max-w-sm" />
          </WidgetOverlay>,
          document.body
        )}
    </>
  );
}
